/**
 * Responsibility: draw an aircraft model in the scene at a given flight state.
 *
 * Owns the conversion from the pure vertex and edge list in `flight/aircraft/` into GPU
 * geometry, and owns the sign and rotation-order conventions that connect the flight
 * model's angles to Three.js transforms.
 *
 * ## Why the signs are not all positive
 *
 * The flight model uses a compass-like heading: 0 faces negative Z and increasing heading
 * turns right. Three.js rotation about +Y turns the other way, so heading is negated.
 * Positive roll means banking right — right wing down — and rotating about the model's +Z
 * axis raises the right wing, so roll is negated too. Pitch needs no flip: rotating about
 * +X raises the nose, which is what positive pitch means.
 *
 * Euler order is `YXZ`, which applies roll in the aircraft's own frame, then pitch, then
 * yaw. Any other order produces a banked aircraft whose pitch axis is wrong, which looks
 * subtly and unfixably broken.
 */

import * as THREE from 'three';

import { AIRCRAFT } from '../config.js';

/** Floats per vertex. */
const COMPONENTS_PER_VERTEX = 3;

/** Vertices per line segment. */
const VERTICES_PER_SEGMENT = 2;

/**
 * Flatten a model's vertex and edge lists into line segment positions.
 *
 * @param {{vertices: Array<Array<number>>, edges: Array<Array<number>>}} model
 * @param {number} scale
 * @returns {Float32Array}
 */
function positionsFor(model, scale) {
  const positions = new Float32Array(
    model.edges.length * VERTICES_PER_SEGMENT * COMPONENTS_PER_VERTEX,
  );

  let write = 0;
  for (const [from, to] of model.edges) {
    for (const index of [from, to]) {
      const vertex = model.vertices[index];
      if (!vertex) {
        // A bad index would otherwise write NaNs and silently blank the whole mesh.
        throw new Error(`aircraftMesh: edge references missing vertex ${index}`);
      }
      positions[write] = vertex[0] * scale;
      positions[write + 1] = vertex[1] * scale;
      positions[write + 2] = vertex[2] * scale;
      write += COMPONENTS_PER_VERTEX;
    }
  }

  return positions;
}

/**
 * Add an aircraft to the scene.
 *
 * @param {THREE.Scene} scene
 * @param {{id: string, vertices: Array, edges: Array}} model
 * @returns {{setPose: Function, dispose: () => void}}
 */
export function createAircraftMesh(scene, model) {
  if (!scene) {
    throw new Error('createAircraftMesh: a scene is required');
  }

  /*
    Fog is off for the aircraft alone. It is always the same short distance from the camera,
    so fog would only ever dim it by a fixed amount — and being the one object that does not
    fade is what makes it read as the thing you are piloting rather than part of the scenery.
  */
  const material = new THREE.LineBasicMaterial({
    color: AIRCRAFT.COLOR,
    fog: false,
    transparent: false,
  });

  const mesh = new THREE.LineSegments(new THREE.BufferGeometry(), material);

  /*
    Never culled. It is a handful of segments, it is never off screen, and the invisible model has an
    empty position buffer from which no meaningful bounding sphere can be derived.
  */
  mesh.frustumCulled = false;
  mesh.rotation.order = 'YXZ';
  scene.add(mesh);

  /** Replace the geometry, releasing whatever was there. */
  function applyModel(next) {
    if (!next || !Array.isArray(next.vertices) || !Array.isArray(next.edges)) {
      throw new Error('aircraftMesh: model must have vertices and edges arrays');
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      'position',
      new THREE.BufferAttribute(positionsFor(next, AIRCRAFT.SCALE), COMPONENTS_PER_VERTEX),
    );

    /*
      Skipped for a model that draws nothing. Three.js derives a NaN-centred sphere from an empty
      position buffer, which produces console warnings for a mesh that is not culled anyway.
    */
    if (next.edges.length > 0) {
      geometry.computeBoundingSphere();
    }

    const previous = mesh.geometry;
    mesh.geometry = geometry;

    // Disposed after the swap, so the mesh is never briefly holding a released buffer.
    previous.dispose();
  }

  applyModel(model);

  return {
    /**
     * Place and orient the aircraft.
     *
     * @param {{x: number, y: number, z: number, heading: number, pitch: number, roll: number}} state
     */
    setPose(state) {
      mesh.position.set(state.x, state.y, state.z);
      mesh.rotation.set(state.pitch, -state.heading, -state.roll);
    },

    /**
     * Swap to a different aircraft, mid-flight.
     *
     * Only the geometry changes. Position, rotation, material, and scene membership are untouched,
     * which is why swapping cannot disturb the flight state or the camera — there is nothing shared
     * to disturb.
     *
     * @param {{vertices: Array, edges: Array}} next
     */
    setModel(next) {
      applyModel(next);
    },

    /**
     * Show or hide the aircraft without discarding it.
     *
     * For train mode, where the aircraft is not in the world at all. Distinct from selecting the invisible
     * model: that is a player's choice about what they fly, and it has to survive a trip on the railway.
     *
     * @param {boolean} visible
     */
    setVisible(visible) {
      mesh.visible = visible;
    },

    /** Segments currently drawn. Zero for the invisible model. Exposed for debug readouts. */
    getSegmentCount() {
      const position = mesh.geometry.getAttribute('position');
      return position ? position.count / VERTICES_PER_SEGMENT : 0;
    },

    /** Release GPU resources. */
    dispose() {
      scene.remove(mesh);
      mesh.geometry.dispose();
      material.dispose();
    },
  };
}
